import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useAdvanceTableContext } from '../../../providers/AdvanceTableProvider';

const getColumnKey = (column) => column.id ?? column.columnDef.accessorKey ?? '';

const isActionsColumn = (column) => {
	const key = getColumnKey(column);
	return key === 'actions' || column.columnDef.header === '';
};

const isPrimaryColumn = (column) => {
	const key = getColumnKey(column);
	const header = column.columnDef.header;
	return (
		column.columnDef.meta?.card?.role === 'primary' ||
		key === 'orderId' ||
		key === 'productCode' ||
		key === 'approval.title' ||
		key === 'approvalTitle' ||
		header === '审批事项' ||
		header === '货号'
	);
};

const isCustomerColumn = (column) => {
	const key = getColumnKey(column);
	const header = column.columnDef.header;
	return (
		column.columnDef.meta?.card?.role === 'customer' ||
		key === 'customerInfo.customerName' ||
		key === 'name' ||
		key === 'productName' ||
		header === '客户名称' ||
		header === '产品名称'
	);
};

const isFooterColumn = (column) => {
	const key = getColumnKey(column);
	return column.columnDef.meta?.card?.role === 'footer' || key === 'createdAt' || key === 'updatedAt';
};

const isStatusColumn = (column) => {
	const key = getColumnKey(column);
	const header = column.columnDef.header;
	return (
		column.columnDef.meta?.card?.role === 'status' ||
		key === 'paymentStatus' ||
		key === 'approvalStatus' ||
		key === 'deliveryStatus' ||
		(typeof header === 'string' && header.endsWith('状态'))
	);
};

const isHiddenOnCard = (column) => column.columnDef.meta?.card?.role === 'hide';

const isAmountColumn = (column) => {
	const key = getColumnKey(column);
	const header = column.columnDef.header;

	if (column.columnDef.meta?.card?.role === 'amount') {
		return true;
	}

	if (['totalPrice', 'prePaymentAmount', 'quasiReceivable', 'accountsReceivable', 'currentAccountsReceivable'].includes(key)) {
		return true;
	}

	return typeof header === 'string' && /金额|总价|应收|预付|准应收/.test(header);
};

const PendingOrderCard = ({ row, rowIndex, rowCount, getRowClassName }) => {
	const cells = row.getVisibleCells();

	const primaryCell = cells.find((cell) => isPrimaryColumn(cell.column) && !isHiddenOnCard(cell.column));
	const customerCell = cells.find((cell) => isCustomerColumn(cell.column) && !isHiddenOnCard(cell.column));
	const actionsCell = cells.find((cell) => isActionsColumn(cell.column));
	const statusCells = cells.filter((cell) => isStatusColumn(cell.column) && !isHiddenOnCard(cell.column));
	const footerCells = cells.filter((cell) => isFooterColumn(cell.column) && !isHiddenOnCard(cell.column));
	const bodyCells = cells.filter((cell) => {
		if (isHiddenOnCard(cell.column)) return false;
		if (cell === primaryCell || cell === customerCell || cell === actionsCell) return false;
		if (statusCells.includes(cell)) return false;
		if (footerCells.includes(cell)) return false;
		if (isActionsColumn(cell.column)) return false;
		return true;
	});

	const rowspan = primaryCell?.column.columnDef.meta?.rowspan?.(rowIndex, { length: rowCount });
	if (rowspan === 0) {
		return null;
	}

	const headerLabel = primaryCell?.column.columnDef.header;
	const customerLabel = customerCell?.column.columnDef.header;

	return (
		<article className={classNames('pending-order-card', getRowClassName?.(row.original))}>
			{(primaryCell || customerCell || statusCells.length > 0 || actionsCell) && (
				<div className='pending-order-card__header'>
					<div className='pending-order-card__heading'>
						{primaryCell || statusCells.length > 0 ? (
							<div className='pending-order-card__title-row'>
								{primaryCell ? (
									<div className='pending-order-card__title' aria-label={typeof headerLabel === 'string' ? headerLabel : '订单'}>
										{flexRender(primaryCell.column.columnDef.cell, primaryCell.getContext())}
									</div>
								) : null}
								{statusCells.length > 0 ? (
									<div className='pending-order-card__statuses'>
										{statusCells.map((cell) => (
											<div className='pending-order-card__status' key={cell.id}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</div>
										))}
									</div>
								) : null}
							</div>
						) : null}
						{customerCell ? (
							<div className='pending-order-card__subtitle' aria-label={typeof customerLabel === 'string' ? customerLabel : '客户'}>
								{flexRender(customerCell.column.columnDef.cell, customerCell.getContext())}
							</div>
						) : null}
					</div>
					{actionsCell ? (
						<div className='pending-order-card__actions' onClick={(event) => event.stopPropagation()}>
							{flexRender(actionsCell.column.columnDef.cell, actionsCell.getContext())}
						</div>
					) : null}
				</div>
			)}

			{bodyCells.length > 0 ? (
				<div className='pending-order-card__body'>
					{bodyCells.map((cell) => {
						const label = cell.column.columnDef.header;
						if (!label) return null;

						return (
							<div
								className={classNames('pending-order-card__field', {
									'pending-order-card__field--amount': isAmountColumn(cell.column),
								})}
								key={cell.id}
							>
								<span className='pending-order-card__label'>{label}</span>
								<span className='pending-order-card__value'>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
							</div>
						);
					})}
				</div>
			) : null}

			{footerCells.length > 0 ? (
				<div className='pending-order-card__footer'>
					{footerCells.map((cell) => {
						const label = cell.column.columnDef.header;
						return (
							<span className='pending-order-card__meta' key={cell.id}>
								{typeof label === 'string' ? `${label} ` : null}
								{flexRender(cell.column.columnDef.cell, cell.getContext())}
							</span>
						);
					})}
				</div>
			) : null}
		</article>
	);
};

PendingOrderCard.propTypes = {
	row: PropTypes.object.isRequired,
	rowIndex: PropTypes.number.isRequired,
	rowCount: PropTypes.number.isRequired,
};

const CategorySegmentSummaryCard = ({ row, getBodySpanGroups }) => {
	const spanGroups = getBodySpanGroups?.(row.original);
	const priceFooterChain = spanGroups?.[0]?.content;
	const visibleCells = row
		.getVisibleCells()
		.filter((cell) => {
			const columnId = cell.column.id;
			const spanGroup = spanGroups?.find((group) => group.columnIds?.includes(columnId));
			if (spanGroups?.length && spanGroup && spanGroup.columnIds[0] !== columnId) return false;
			return true;
		});

	return (
		<div className='pending-order-card pending-order-card__summary pending-order-card__summary--segment border-top pt-2 mt-2'>
			<div className='pending-order-card__body'>
				{visibleCells.map((cell) => {
					const columnId = cell.column.id;
					const spanGroup = spanGroups?.find((group) => group.columnIds?.includes(columnId));
					const content = spanGroup ? spanGroup.content : flexRender(cell.column.columnDef.cell, cell.getContext());
					if (content == null || content === '') return null;

					return (
						<div
							className={classNames('pending-order-card__field', {
								'pending-order-card__field--amount': spanGroup != null || isAmountColumn(cell.column),
								'fw-bold': columnId === 'productCode',
							})}
							key={cell.id}
						>
							{typeof cell.column.columnDef.header === 'string' && !spanGroup ? (
								<span className='pending-order-card__label'>{cell.column.columnDef.header}</span>
							) : null}
							<span className={classNames('pending-order-card__value', spanGroup ? 'w-100 text-center' : undefined)}>{content}</span>
						</div>
					);
				})}
				{!spanGroups?.length && priceFooterChain ? (
					<div className='pending-order-card__field pending-order-card__field--amount d-flex justify-content-center'>
						<span className='pending-order-card__value fw-bold w-100 text-center'>{priceFooterChain}</span>
					</div>
				) : null}
			</div>
		</div>
	);
};

CategorySegmentSummaryCard.propTypes = {
	row: PropTypes.object.isRequired,
	getBodySpanGroups: PropTypes.func,
};

const AdvanceTableCardList = ({ className, footerCells, footerSpanGroups, getBodySpanGroups, getRowClassName }) => {
	const table = useAdvanceTableContext();

	const rows = useMemo(() => {
		try {
			return table.getRowModel()?.rows ?? [];
		} catch {
			return [];
		}
	}, [table]);

	const priceFooterChain = footerSpanGroups?.[0]?.content;
	const hasPriceFooter =
		priceFooterChain ||
		footerCells?.purchasingPrice ||
		footerCells?.sellingPrice ||
		footerCells?.lineTotalPrice ||
		footerCells?.productName;

	if (rows.length === 0) {
		return null;
	}

	return (
		<div className={classNames('pending-order-card-list', className)}>
			{rows.map((row, rowIndex) =>
				row.original?.rowType === 'categorySubtotal' ? (
					<CategorySegmentSummaryCard key={row.id} row={row} getBodySpanGroups={getBodySpanGroups} />
				) : (
					<PendingOrderCard key={row.id} row={row} rowIndex={rowIndex} rowCount={rows.length} getRowClassName={getRowClassName} />
				),
			)}
			{hasPriceFooter ? (
				<div className='pending-order-card pending-order-card__summary border-top pt-2 mt-2'>
					<div className='pending-order-card__body'>
						{footerCells?.productCode ? (
							<div className='pending-order-card__field fw-bold'>
								<span className='pending-order-card__label'>{footerCells.productCode}</span>
							</div>
						) : null}
						{footerCells?.productName ? (
							<div className='pending-order-card__field pending-order-card__field--amount'>
								<span className='pending-order-card__value w-100'>{footerCells.productName}</span>
							</div>
						) : null}
						{priceFooterChain ? (
							<div className='pending-order-card__field pending-order-card__field--amount d-flex justify-content-center'>
								<span className='pending-order-card__value fw-bold w-100 text-center'>{priceFooterChain}</span>
							</div>
						) : null}
					</div>
				</div>
			) : null}
		</div>
	);
};

AdvanceTableCardList.propTypes = {
	className: PropTypes.string,
	footerCells: PropTypes.object,
	footerSpanGroups: PropTypes.array,
	getBodySpanGroups: PropTypes.func,
	getRowClassName: PropTypes.func,
};

export default AdvanceTableCardList;
